import type { GameProps } from '../../types';
import type { FrameProps } from './Frame';

import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../contexts/AppContext';
import { GameOrientationEnum } from '../../types/enum';
import { addOrReplaceParam } from '../../utils/add-or-replace-params';
import Ancestor from '../../utils/ancestor';
import useLocalStorage from '../../utils/use-local-storage';
import { OrientationLock, unsetScreenLock } from '../OrientationLock';
import { DraggableMenuButton } from './DraggableMenuButton';
import { Frame } from './Frame';
import { HARDCODED_GAMES } from './hardcoded-games';
import { StagePopup } from './stagePopUp';
import { RedirectBackButton } from './RedirectBackButton';
import { isMobile, MobileView } from 'react-device-detect';

export interface GameFrameProps {
  clickToPlay?: boolean;
  game: GameProps;
  start?: boolean;
  enableOrientationLock?: boolean;
  enableDraggableMenu?: boolean;
  enableResponsive?: boolean;
  onStop?: Function;
  fixedHeight?: boolean;
}

interface IframeProps extends FrameProps {
  started: boolean;
  menu: boolean;
  orientation: GameOrientationEnum;
}

interface ContainerProps {
  fullscreen?: boolean;
  fixedHeight?: boolean;
}

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  '& svg': {
    fill: theme.palette.primary.contrastText,
    fontSize: theme.typography.button.fontSize,
  },
  zIndex: 1201,
  width: "30px",
  height: "30px",
  left: "10px",
  top: "50px"
})) as typeof IconButton;

const NextLevelModal = styled('div')(({ theme }) => ({
  zIndex: 5,
  background: 'rgba(0, 0, 0, 0.5)',
  width: '100%',
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'center',
}));

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-grow: 1;
  overflow: auto;
  position: relative;
  z-index: inherit;
  top: auto;
  bottom: auto;
  right: auto;
  left: auto;
  aspect-ratio: 16/9;
  margin: 0;
  width: 100%;
  ${(props: ContainerProps) =>
    props.fullscreen && {
      overflow: 'hidden',
      position: 'fixed',
      zIndex: 2,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      aspectRatio: 'unset',
    }};
  ${(props: ContainerProps) =>
    props.fixedHeight && {
      height: '600px',
      aspectRatio: '1',
    }};
`;

export const GameFrame = (props: GameFrameProps) => {
  const {
    game,
    start = false,
    enableOrientationLock = false,
    enableDraggableMenu = false,
    enableResponsive = false,
    onStop,
    fixedHeight,
  } = props;

  const [ancestor] = React.useState(new Ancestor());
  const { i18n } = useTranslation();
  const container = React.useRef<HTMLDivElement>(null);
  const iframe = React.createRef<HTMLIFrameElement>();
  const [frame, setFrame] = React.useState<IframeProps>({
    started: false,
    src: '',
    width: '100%',
    height: '100%',
    elementWidth: '100%',
    elementHeight: '100%',
    menu: false,
    orientation: GameOrientationEnum.ANY,
  });
  const [gamesCurrent, setGamesCurrent] = useLocalStorage<GameProps[]>('lastplayed:current', []);
  const toggleMenu = React.useCallback(() => {
    const handler = async () => {
      try {
        await unsetScreenLock();
      } catch (error) {
        // Silently fail.
        console.error(error);
      }

      // Todo: Currently don't have a menu interface. So lets just close the game.
      setFrame((state) => ({
        ...state,
        menu: !state.menu,
        started: false,
      }));
    };

    handler();

    if (onStop) {
      onStop(frame.started);
    }
  }, [setFrame]);

  const addGameToLastPlayed = (game: GameProps) => {
    const isAlreadyExist: boolean = gamesCurrent.some((element: GameProps) => element.id === game.id);

    if (isAlreadyExist) {
      const index: number = gamesCurrent.findIndex((element: GameProps) => element.id === game.id);
      if (index === 0) return; // Game already exists and is the first one already.

      // Move the game to the top of the list.
      const item = gamesCurrent.splice(index, 1);
      // @ts-ignore Todo: Not sure how to fix this typescript issue.
      gamesCurrent.unshift(item);
    } else {
      if (gamesCurrent.length > 5) gamesCurrent.pop(); // Only max out the list to 5 items.
      gamesCurrent.unshift(game);
      setGamesCurrent([...gamesCurrent]);
    }
  };

  React.useEffect(() => {
    setFrame((state) => ({
      ...state,
      started: start,
    }));
  }, [start]);

  React.useEffect(() => {
    const overwrite = HARDCODED_GAMES.find((item) => item.code === game.code);

    let orientation = game.orientation;
    if (overwrite && overwrite.orientation) {
      orientation = overwrite.orientation;
    }

    setFrame((state) => ({ ...state, orientation }));

    if (game.src) {
      let src = game.src;

      // Need to set the "gd_sdk_referrer_url" parameter. As referrer header will not be present all the time.
      // It is needed for reporting purposes. The page href is preferred over the site origin.
      src = addOrReplaceParam(
        src,
        'gd_sdk_referrer_url',
        ancestor.context?.url || document.location.href || document.location.origin
      );

      // Add language parameter, for usage by the GD SDK and Google IMA SDK.
      src = addOrReplaceParam(src, 'lang', i18n.language);

      setFrame((state) => ({ ...state, src }));
    }
  }, [game, i18n.language]);

  const [openNextLevelModal, setOpenNextLevelModal] = React.useState<boolean>(false);

  React.useEffect(() => {
    /**
     * Special logic for SoftGames crossword puzzles. We need to set our game frame
     * height for these games, as they are not responsive.
     * They send a PostMessage event with the height based on mobile agents.
     */
    const messageHandler = (event: MessageEvent) => {
      if (event.origin === 'https://games.softgames.com' || event.origin === 'https://html5.gamedistribution.com') {
        try {
          console.log('GD+ type', typeof event.data);
          console.log('GD+ event data', event.data);
          const data = typeof event.data === 'object' ? event.data : undefined;
          if (data && data.height) {
            setFrame((state) => ({
              ...state,
              height: `${data.height}px`,
              elementHeight: data.height,
              orientation: GameOrientationEnum.PORTRAIT,
            }));
            if (container.current) {
              container.current.style.overflow = 'auto';
            }
          }
          if (
            (data.eventName === 'levelFinish') &&
            componentOptions.component_gamescreen_next_level_modal
          ) {
            setOpenNextLevelModal(true);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };

    if (
      frame.src !== '' &&
      (frame.src.includes('https://games.softgames.com') || frame.src.includes('https://html5.gamedistribution.com'))
    ) {
      window.addEventListener('message', messageHandler);
    }

    return () => {
      if (
        frame.src !== '' &&
        (frame.src.includes('https://games.softgames.com') || frame.src.includes('https://html5.gamedistribution.com'))
      ) {
        window.removeEventListener('message', messageHandler);
        if (container.current) {
          container.current.style.overflow = 'hidden';
        }
      }
    };
  }, [frame.src]);

  React.useEffect(() => {
    const resizeHandler = () => {
      setFrame((state) => ({
        ...state,
        width: `${document.documentElement.clientWidth}px`,
        height: `${document.documentElement.clientHeight}px`,
        elementWidth: document.documentElement.clientWidth,
        elementHeight: document.documentElement.clientHeight,
      }));
    };

    if (frame.started && enableResponsive) {
      resizeHandler();
      window.addEventListener('resize', resizeHandler);
    } else {
      window.removeEventListener('resize', resizeHandler);
    }

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [setFrame, frame.started, enableResponsive]);

  React.useEffect(() => {
    if (frame.started && iframe.current) {
      iframe.current.focus();
    }
  }, [frame.started, iframe]);

  React.useEffect(() => {
    if (frame.started) {
      addGameToLastPlayed(game);
    }
  }, [game, frame.started]);

  const appContext = useAppContext();
  const { assetUrl, componentOptions } = appContext.useConfig();

  if (!frame.started) return <></>;

  const redirectButtonShow = componentOptions?.component_gamescreen_redirect_back_button_show;

  return (
    <Container style={{ zIndex: isMobile ? "1200" : ""}} ref={container} fullscreen={enableOrientationLock} fixedHeight={fixedHeight}>
      {enableOrientationLock && container.current && (
        <OrientationLock fullscreenElement={container.current} zIndex={3} orientation={frame.orientation} />
      )}
      {enableDraggableMenu && (
        <IconButtonStyled className="closebutton" onClick={toggleMenu}>
          <CloseIcon />
        </IconButtonStyled>
      )}
      {openNextLevelModal && (
        <StagePopup
          iconUrl={`${assetUrl}${game.images.cover1x1 || game.images.cover}/`}
          name={`${game.name}`}
          closePopUp={() => setOpenNextLevelModal(false)}
        />
      )}
      <MobileView>
        {redirectButtonShow && <RedirectBackButton />}
      </MobileView>
      <Frame
        ref={iframe}
        src={frame.src}
        elementWidth={frame.elementWidth}
        elementHeight={frame.elementHeight}
        width={frame.width}
        height={frame.height}
      />
    </Container>
  );
};
